import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';

// "Can't shoot" reasons (alphabetical) — mirrors the mobile app's list.
const SHOOT_REASONS = [
  'At auction', 'Awaiting recon', 'Being detailed', 'Demo/Loaner', 'In service',
  'In transit', 'Mechanical issue', 'Missing keys', 'Not on lot', 'On test drive',
  'Parts on order', 'Recall hold', 'Sold/Pending delivery', 'Wholesale', 'Other',
];

export default function PhotographerVehicle() {
  const { dealerId, vin } = useParams();
  const navigate = useNavigate();
  // The inventory view's selection rides along on this URL's query string, so going back
  // restores the exact list she left -- photographer, dealership, search, filter and sort.
  // Carried in the URL rather than router state so it also survives a refresh and a
  // pasted link. See src/utils/inventoryQuery.js.
  const { search: returnQuery } = useLocation();
  const backToInventory = () => navigate(`/photographer${returnQuery || ''}`);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState({});

  const load = useCallback(() => {
    api.getPhotographerVehicle(dealerId, vin)
      .then((d) => {
        setData(d);
        const v = d.vehicle || {};
        setFields({
          year: v.year, make: v.make, model: v.model, trim: v.trim,
          exterior_color: v.exterior_color, interior_color: v.interior_color,
          mileage: v.mileage, price: v.price,
          comments: d.custom_fields?.notes || '',
        });
      })
      .catch((e) => setError(e.message));
  }, [dealerId, vin]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div style={{ padding: 20, color: '#dc2626' }}>{error} <button onClick={backToInventory}>Back</button></div>;
  if (!data) return <div style={{ padding: 20 }}>Loading…</div>;
  const { vehicle, photos = [], videos = [] } = data;

  const saveFields = async () => {
    setBusy(true);
    try { await api.updatePhotographerVehicle(dealerId, vin, fields); await load(); alert('Saved'); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const delPhoto = async (id) => { if (!window.confirm('Remove this photo?')) return; await api.deletePhotographerPhoto(dealerId, vin, id); load(); };
  const delVideo = async (id) => { if (!window.confirm('Remove this video?')) return; await api.deletePhotographerVideo(dealerId, vin, id); load(); };
  const move = async (idx, dir) => {
    const ids = photos.map((p) => p.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setBusy(true);
    try { await api.reorderPhotographerPhotos(dealerId, vin, ids); await load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const onUploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try { for (const f of files) await api.uploadPhotographerPhoto(dealerId, vin, f); await load(); }
    catch (err) { alert(err.message); } finally { setBusy(false); e.target.value = ''; }
  };
  const onUploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const type = window.confirm('OK = Walkaround video, Cancel = Interior video') ? 'walkaround' : 'interior';
    setBusy(true);
    try { await api.uploadPhotographerVideo(dealerId, vin, f, type); await load(); }
    catch (err) { alert(err.message); } finally { setBusy(false); e.target.value = ''; }
  };
  const onShootReason = async (e) => {
    const reason = e.target.value;
    e.target.value = '';  // reset the select
    if (!reason) return;
    setBusy(true);
    try { await api.setShootStatus(dealerId, vin, reason); await load(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  };
  const clearShoot = async () => {
    setBusy(true);
    try { await api.clearShootStatus(dealerId, vin); await load(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  };
  const blockedReason = data.custom_fields?.shoot_blocked_reason || null;

  return (
    <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
      <button onClick={backToInventory} style={{ marginBottom: 12 }}>← Back to inventory</button>
      <h2>{vehicle.year} {vehicle.make} {vehicle.model}</h2>
      <div style={{ color: '#6b7280', marginBottom: 12 }}>VIN {vin} · Stock {vehicle.stock_number || '—'}</div>

      {/* "Can't shoot" status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {blockedReason ? (
          <>
            <span style={blockedPill}>🚫 Can't shoot: {blockedReason}</span>
            <button onClick={clearShoot} disabled={busy} style={btn}>Mark shootable</button>
          </>
        ) : (
          <label style={{ ...lbl, margin: 0 }}>Can't shoot?
            <select onChange={onShootReason} disabled={busy} defaultValue="" style={{ ...inp, marginLeft: 8 }}>
              <option value="">— pick a reason —</option>
              {SHOOT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}
      </div>

      <h3>Details</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
        {['year', 'make', 'model', 'trim', 'exterior_color', 'interior_color', 'mileage', 'price'].map((k) => (
          <label key={k} style={lbl}>{k.replace(/_/g, ' ')}
            <input value={fields[k] ?? ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} style={inp} />
          </label>
        ))}
      </div>
      <label style={{ ...lbl, display: 'block', marginTop: 10 }}>photographer notes
        <textarea value={fields.comments || ''} onChange={(e) => setFields({ ...fields, comments: e.target.value })} style={{ ...inp, height: 60 }} />
      </label>
      <button onClick={saveFields} disabled={busy} style={btn}>Save details</button>

      <h3 style={{ marginTop: 24 }}>Photos ({photos.length})
        <label style={uploadBtn}>+ Add photos<input type="file" accept="image/*" multiple onChange={onUploadPhoto} style={{ display: 'none' }} /></label>
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12 }}>
        {photos.map((p, i) => (
          <div key={p.id} style={tile}>
            <img src={p.thumbnail_url || p.url} alt="" style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 4 }} />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{p.source === 'feed' ? 'Feed' : 'Shot'}{p.is_stock ? ' · Stock' : ''}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <button onClick={() => move(i, -1)} disabled={i === 0 || busy} style={miniBtn}>◀</button>
              <button onClick={() => delPhoto(p.id)} disabled={busy} style={{ ...miniBtn, color: '#dc2626' }}>🗑</button>
              <button onClick={() => move(i, 1)} disabled={i === photos.length - 1 || busy} style={miniBtn}>▶</button>
            </div>
          </div>
        ))}
        {!photos.length && <div style={{ color: '#6b7280' }}>No photos.</div>}
      </div>

      <h3 style={{ marginTop: 24 }}>Videos ({videos.length})
        <label style={uploadBtn}>+ Add video<input type="file" accept="video/*" onChange={onUploadVideo} style={{ display: 'none' }} /></label>
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {videos.map((v) => (
          <div key={v.id} style={tile}>
            <video src={v.url} controls style={{ width: '100%', borderRadius: 4 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{v.video_subtype}</span>
              <button onClick={() => delVideo(v.id)} disabled={busy} style={{ ...miniBtn, color: '#dc2626' }}>🗑 Remove</button>
            </div>
          </div>
        ))}
        {!videos.length && <div style={{ color: '#6b7280' }}>No videos.</div>}
      </div>

      {busy && <div style={toast}>Working…</div>}
    </div>
  );
}

const lbl = { fontSize: 12, color: '#6b7280' };
const inp = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 2 };
const btn = { marginTop: 12, padding: '8px 16px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const uploadBtn = { marginLeft: 12, fontSize: 13, fontWeight: 'normal', padding: '4px 10px', background: '#0078D4', color: '#fff', borderRadius: 6, cursor: 'pointer' };
const tile = { border: '1px solid #e5e7eb', borderRadius: 8, padding: 6 };
const miniBtn = { background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', padding: '2px 6px' };
const toast = { position: 'fixed', bottom: 20, right: 20, background: '#111827', color: '#fff', padding: '8px 16px', borderRadius: 6 };
const blockedPill = { background: 'rgba(208,2,27,0.92)', color: '#fff', fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 6 };
